import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Utilitário para mesclar classes Tailwind de forma inteligente
 * Combina clsx (condicionais) com tailwind-merge (resolve conflitos)
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formata valor para moeda brasileira
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

/**
 * Formata valor para USDC (dólar)
 */
export function formatUSDC(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Formata centavos para reais
 */
export function formatCentsToReais(cents: number): string {
  return formatCurrency(cents / 100);
}

/**
 * Formata valor para porcentagem
 */
export function formatPercent(value: number, decimals = 2): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "percent",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value / 100);
}

/**
 * Formata data para o padrão brasileiro
 * IMPORTANTE: Datas no formato YYYY-MM-DD são interpretadas como UTC pelo JavaScript.
 * Adicionamos T12:00:00 para evitar problemas de timezone (meia-noite UTC vira dia anterior no Brasil).
 */
export function formatDate(date: Date | string | null | undefined): string {
  if (!date) return "—";
  let d: Date;
  if (typeof date === "string") {
    // Se for formato YYYY-MM-DD (sem hora), adiciona meio-dia para evitar problema de timezone
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      d = new Date(date + "T12:00:00");
    } else {
      d = new Date(date);
    }
  } else {
    d = date;
  }
  return new Intl.DateTimeFormat("pt-BR").format(d);
}

/**
 * Formata data e hora para o padrão brasileiro
 */
export function formatDateTime(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(d);
}

/**
 * Formata CNPJ
 */
export function formatCNPJ(cnpj: string): string {
  const cleaned = cnpj.replace(/\D/g, "");
  return cleaned.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5"
  );
}

/**
 * Formata CPF
 */
export function formatCPF(cpf: string): string {
  const cleaned = cpf.replace(/\D/g, "");
  return cleaned.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
}

/**
 * Delay para async/await
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
